// packages/shared/src/events.ts
// Pure pricing + inventory helpers for prepaid event tickets (special-menu
// covers — Valentine's, Mother's Day, NYE, …). No I/O: these run identically in
// the widget (against PublicEventTicketing) and the middleware (against the full
// EventTicketing), so the price and availability the guest sees can never
// disagree with what the server charges and enforces.

import type { EventTicketTier, EventAddOn, PublicEventTicketTier } from './types.js';

/**
 * Minimal pricing shape common to EventTicketing (server, full) and
 * PublicEventTicketing (widget projection). The helpers below take this
 * structural type so both layers share one source of truth for ticket pricing.
 */
export interface TicketPricing {
  currency?: string;
  pricePerPersonCents: number;
  // Optional flat kids rate (see EventTicketing.kidsPricePerPersonCents).
  // Absent ⇒ kids are quoted at the adult/tier unit price, unchanged.
  kidsPricePerPersonCents?: number;
  minPartySize?: number;
  maxPartySize?: number;
  tiers?: EventTicketTier[];
}

export type TicketQuoteReason =
  | 'invalid_party_size'
  | 'party_below_min'
  | 'party_above_max'
  | 'tier_required'
  | 'invalid_tier'
  | 'invalid_price'
  | 'invalid_kids_count'
  // #652 G3 BLOCK 2 — system bounds on a BASKET. They live on the quote so
  // /quote and /checkout agree and the read boundary's limits can never be
  // exceeded by something the server itself produced.
  | 'too_many_ticket_types'
  | 'basket_too_large';

export interface TicketQuote {
  ok: boolean;
  reason?: TicketQuoteReason;   // populated when ok === false
  unitPriceCents?: number;      // per-person cover price (adults)
  kidsUnitPriceCents?: number;  // per-kid cover price; equals unitPriceCents when no kids rate is configured
  kidsCount?: number;           // echoes the quoted kidsCount (0 when absent)
  totalCents?: number;          // unitPriceCents * adults + kidsUnitPriceCents * kidsCount
  currency?: string;
  partySize?: number;
  tierId?: string;
  tierLabel?: string;
}

function isUsablePrice(n: unknown): n is number {
  return typeof n === 'number' && Number.isInteger(n) && n > 0;
}

function isUsableKidsPrice(n: unknown): n is number {
  return typeof n === 'number' && Number.isInteger(n) && n >= 0;
}

/**
 * Resolve the per-person cover price (cents) for a ticket, honouring tiers.
 * When tiers are defined the guest MUST choose one (the base pricePerPersonCents
 * is only used when there are no tiers). Returns null when a tier is
 * required-but-missing, the tier is unknown, or the price is unusable.
 */
export function resolveTicketUnitPriceCents(
  pricing: TicketPricing | undefined | null,
  tierId?: string,
): number | null {
  if (!pricing) return null;
  const tiers = pricing.tiers ?? [];
  if (tiers.length > 0) {
    if (!tierId) return null;                 // a tier must be chosen
    const tier = tiers.find(t => t.id === tierId);
    if (!tier) return null;                   // unknown tier
    return isUsablePrice(tier.pricePerPersonCents) ? tier.pricePerPersonCents : null;
  }
  if (tierId) return null;                    // tier supplied but event has none
  return isUsablePrice(pricing.pricePerPersonCents) ? pricing.pricePerPersonCents : null;
}

/**
 * Quote a ticket purchase: validate party-size bounds and tier selection, then
 * return the authoritative unit + total price. Never throws — failures come back
 * as `{ ok: false, reason }` so callers can map them to user-facing errors.
 */
export function quoteEventTicket(
  pricing: TicketPricing | undefined | null,
  partySize: number,
  tierId?: string,
  kidsCount = 0,
): TicketQuote {
  if (!Number.isInteger(partySize) || partySize < 1) {
    return { ok: false, reason: 'invalid_party_size' };
  }
  if (!Number.isInteger(kidsCount) || kidsCount < 0 || kidsCount > partySize) {
    return { ok: false, reason: 'invalid_kids_count' };
  }
  // G3 payments HIGH-1 / dynamo M2 — the same absolute bound the BASKET quote
  // enforces. It was added only to `quoteEventTicketLines`, so this legacy
  // scalar path — still taken by every cached-old-bundle request and by every
  // NON-TIERED event — stayed capped only by the OPTIONAL `maxPartySize`.
  //
  // Two shapes got through, both driven against the live route by the reviewer:
  //   1e21  `Number.isInteger` is true, an intent was minted for 1e25 cents,
  //         then `marshall()` threw a PLAIN Error -> 500 AFTER the charge.
  //   9e14  a SAFE integer, so nothing threw anywhere and the row PERSISTED.
  //         `seedCovers` then adds 9e14 covers to the event total, which reads
  //         permanently sold out with no operator remedy — EVTCAP#'s TTL runs
  //         past the event date, so it never re-seeds and corrects itself.
  //
  // A bound on one of two producers is not a bound.
  if (partySize > MAX_SEATS_PER_BASKET) {
    return { ok: false, reason: 'basket_too_large' };
  }

  const min = pricing?.minPartySize ?? 1;
  const max = pricing?.maxPartySize;
  if (partySize < min) return { ok: false, reason: 'party_below_min' };
  if (typeof max === 'number' && partySize > max) return { ok: false, reason: 'party_above_max' };

  const tiers = pricing?.tiers ?? [];
  const unit = resolveTicketUnitPriceCents(pricing, tierId);
  if (unit == null) {
    if (tiers.length > 0 && !tierId) return { ok: false, reason: 'tier_required' };
    if (tierId && !tiers.some(t => t.id === tierId)) return { ok: false, reason: 'invalid_tier' };
    return { ok: false, reason: 'invalid_price' };
  }

  // Flat kids rate applies regardless of tier. Absent/unusable ⇒ kids are
  // quoted at the adult unit price (today's behavior, unchanged).
  const kidsUnit = isUsableKidsPrice(pricing?.kidsPricePerPersonCents)
    ? pricing!.kidsPricePerPersonCents!
    : unit;
  const adults = partySize - kidsCount;

  const tierLabel = tierId ? tiers.find(t => t.id === tierId)?.label : undefined;
  const quote: TicketQuote = {
    ok: true,
    unitPriceCents: unit,
    kidsUnitPriceCents: kidsUnit,
    kidsCount,
    totalCents: (unit * adults) + (kidsUnit * kidsCount),
    partySize,
  };
  if (pricing?.currency) quote.currency = pricing.currency;
  if (tierId) quote.tierId = tierId;
  if (tierLabel) quote.tierLabel = tierLabel;
  return quote;
}

// ── Mixed-tier baskets (#652) ────────────────────────────────────────────────

/**
 * Max DISTINCT ticket types in one basket, and max seats in one basket.
 *
 * #652 G3 BLOCK 2 — these were enforced ONLY on the read boundary in the
 * middleware, which meant the server could happily quote and charge a basket it
 * would then refuse to read back: nine ticket types quoted `ok`, minted a
 * payment intent, and threw on the next read as a 500 — an orphaned intent and
 * a poisoned row. A bound enforced only on the consumer is not a bound.
 *
 * `services/event-tickets.ts` IMPORTS these — it does not keep its own copy.
 * It did, briefly, while this comment already claimed otherwise, which is
 * exactly how two constants with the same intent drift apart (G3 dynamo H1).
 */
export const MAX_TICKET_TYPES_PER_BASKET = 8;
export const MAX_SEATS_PER_BASKET = 500;

/** One line of a mixed-tier basket as the CLIENT sends it. Quantities only —
 *  never a price, never a total (DR-PAYMENTS-01). */
export interface TicketLineInput {
  tierId: string;
  /** SEATS on this tier, children included. */
  qty: number;
  /** How many of those seats are children. Defaults to 0. */
  kidsQty?: number;
}

/** One priced line, resolved server-side from stored config. */
export interface TicketQuoteLine {
  tierId: string;
  tierLabel?: string;
  qty: number;
  kidsQty: number;
  unitPriceCents: number;
  kidsUnitPriceCents: number;
  subtotalCents: number;
}

export interface TicketLinesQuote {
  ok: boolean;
  reason?: TicketQuoteReason;
  lines?: TicketQuoteLine[];
  totalCents?: number;
  partySize?: number;
  kidsCount?: number;
  currency?: string;
}

/**
 * #652 — price a basket that mixes ticket types: 2 x VIP + 1 x Standard in one
 * booking.
 *
 * WHY `kidsQty` IS PER LINE, when the approved plan (§4.3 option A) said kids
 * stay orthogonal and this function keeps them orthogonal in PRICE:
 *
 * A kid still pays ONE flat rate whatever tier they sit on — that is option A,
 * and it is unchanged. What the per-line count records is not a price but a
 * SEAT: which tier's inventory the child occupies. The G3 dynamo review (F2)
 * established that `qty` must count seats INCLUDING children, or a single
 * `EVTCAP#…#TIER#…` counter would accumulate seats from legacy rows and adults
 * from new ones. Once a child occupies a specific tier's seat, a single
 * event-level `kidsCount` no longer determines the price:
 *
 *   2 x VIP (R500) + 1 x Standard (R200), one child at R100
 *     child on the Standard seat -> 500 + 500 + 100 = R1100
 *     child on a VIP seat        -> 500 + 200 + 100 =  R800
 *
 * Both readings are defensible, which is exactly the problem: the server must
 * charge ONE defensible amount, and any tie-break we invent (cheapest-first,
 * dearest-first, input order) is either arbitrary or gameable by reordering.
 * Asking the guest which seat the child takes is the only answer that is never
 * a surprise on the bill, and the stepper UI has to ask anyway to get the
 * inventory right.
 *
 * A legacy scalar booking reads back as one line with `kidsQty = kidsCount`,
 * which reproduces today's total exactly.
 *
 * Pure and never throws — failures come back as `{ ok: false, reason }`, the
 * same reason codes `quoteEventTicket` uses, so callers map errors one way.
 */
export function quoteEventTicketLines(
  pricing: TicketPricing | undefined | null,
  lines: readonly TicketLineInput[] | undefined | null,
): TicketLinesQuote {
  if (!Array.isArray(lines) || lines.length === 0) {
    return { ok: false, reason: 'tier_required' };
  }

  // Merge before pricing so a repeated tier is one line, matching the read
  // boundary in services/event-tickets.ts (a transaction may not touch the
  // same counter item twice).
  const merged: Array<{ tierId: string; qty: number; kidsQty: number }> = [];
  for (const line of lines) {
    const tierId = typeof line?.tierId === 'string' ? line.tierId : '';
    const qty = line?.qty;
    const kidsQty = line?.kidsQty ?? 0;
    if (!tierId) return { ok: false, reason: 'invalid_tier' };
    // `typeof === 'number'` rather than Number(): the coercion accepts `true`
    // as 1 and `''` as 0.
    if (typeof qty !== 'number' || !Number.isInteger(qty) || qty < 1) {
      return { ok: false, reason: 'invalid_party_size' };
    }
    if (qty > MAX_SEATS_PER_BASKET) {
      return { ok: false, reason: 'basket_too_large' };
    }
    if (typeof kidsQty !== 'number' || !Number.isInteger(kidsQty) || kidsQty < 0 || kidsQty > qty) {
      return { ok: false, reason: 'invalid_kids_count' };
    }
    const existing = merged.find((m) => m.tierId === tierId);
    if (existing) { existing.qty += qty; existing.kidsQty += kidsQty; }
    else merged.push({ tierId, qty, kidsQty });
  }

  // Bounds FIRST, on the merged shape — a repeated tier is one type, and the
  // seat total is what reaches `partySize` and every counter.
  if (merged.length > MAX_TICKET_TYPES_PER_BASKET) {
    return { ok: false, reason: 'too_many_ticket_types' };
  }
  const partySize = merged.reduce((n, m) => n + m.qty, 0);
  const kidsCount = merged.reduce((n, m) => n + m.kidsQty, 0);
  // `Number.isInteger(1e21)` is TRUE, and 1e21 reaches DynamoDB's `marshall()`,
  // which throws a PLAIN Error — not a sold-out signal — so it escaped as a 500
  // AFTER the payment intent was minted. Capping the TOTAL closes that for the
  // sum as well as for any single line.
  if (partySize > MAX_SEATS_PER_BASKET) {
    return { ok: false, reason: 'basket_too_large' };
  }

  // Party bounds are judged on the WHOLE basket, not per line — a venue's
  // "max 8 per booking" means eight people, however they split across tiers.
  const min = pricing?.minPartySize ?? 1;
  const max = pricing?.maxPartySize;
  if (partySize < min) return { ok: false, reason: 'party_below_min' };
  if (typeof max === 'number' && partySize > max) return { ok: false, reason: 'party_above_max' };

  const tiers = pricing?.tiers ?? [];
  const out: TicketQuoteLine[] = [];
  let totalCents = 0;

  for (const m of merged) {
    const unit = resolveTicketUnitPriceCents(pricing, m.tierId);
    if (unit == null) {
      // Distinguish "no such tier" from "tier exists but is unpriced", exactly
      // as the single-tier quote does.
      if (!tiers.some((t) => t.id === m.tierId)) return { ok: false, reason: 'invalid_tier' };
      return { ok: false, reason: 'invalid_price' };
    }
    const kidsUnit = isUsableKidsPrice(pricing?.kidsPricePerPersonCents)
      ? pricing!.kidsPricePerPersonCents!
      : unit;
    const subtotalCents = unit * (m.qty - m.kidsQty) + kidsUnit * m.kidsQty;
    totalCents += subtotalCents;

    const line: TicketQuoteLine = {
      tierId: m.tierId,
      qty: m.qty,
      kidsQty: m.kidsQty,
      unitPriceCents: unit,
      kidsUnitPriceCents: kidsUnit,
      subtotalCents,
    };
    const label = tiers.find((t) => t.id === m.tierId)?.label;
    if (label) line.tierLabel = label;
    out.push(line);
  }

  const quote: TicketLinesQuote = { ok: true, lines: out, totalCents, partySize, kidsCount };
  if (pricing?.currency) quote.currency = pricing.currency;
  return quote;
}

// ── Inventory (pure) ─────────────────────────────────────────────────────────

// ── Add-ons (pure pricing) — R11.5 [P1 scaffold] ─────────────────────────────
// Server-authoritative add-on pricing. The widget sends only { id, quantity };
// the server resolves the unit price from the event's addOns config here, so the
// guest can never influence the amount. Pure + side-effect-free, like the ticket
// helpers above, so the widget preview and the server charge agree exactly.

export interface AddOnSelection {
  id: string;
  quantity: number;
}

export interface AddOnQuoteLine {
  id: string;
  label: string;
  unitPriceCents: number;
  quantity: number;
  totalCents: number;
}

export interface AddOnQuote {
  ok: boolean;
  reason?: 'unknown_addon' | 'invalid_quantity' | 'over_max';
  lines: AddOnQuoteLine[];
  totalCents: number;
}

/**
 * Resolve the authoritative price of a set of add-on selections against the
 * event's configured add-ons. `partySize` lets perGuest add-ons default their
 * quantity. Never throws — invalid selections come back as { ok: false, reason }.
 *
 * TODO(R11.5): wire this into /api/widget/ticket/checkout so add-on revenue is
 * priced here and stored on reservation.customFields.addOns (alongside the
 * existing ticketTier* fields); the per-event EVTCAP cover counter is unchanged
 * (add-ons ride customFields, not capacity). Coordinate the stored shape with
 * the View 10 widget teammate before enabling in the UI.
 */
export function quoteAddOns(
  addOns: EventAddOn[] | undefined | null,
  selections: AddOnSelection[] | undefined | null,
  partySize: number,
): AddOnQuote {
  const lines: AddOnQuoteLine[] = [];
  if (!selections || selections.length === 0) return { ok: true, lines, totalCents: 0 };
  const byId = new Map((addOns ?? []).map((a) => [a.id, a]));

  for (const sel of selections) {
    const addOn = byId.get(sel.id);
    if (!addOn) return { ok: false, reason: 'unknown_addon', lines: [], totalCents: 0 };
    const qty = sel.quantity ?? (addOn.perGuest ? partySize : 1);
    if (!Number.isInteger(qty) || qty < 1) return { ok: false, reason: 'invalid_quantity', lines: [], totalCents: 0 };
    if (typeof addOn.maxQuantity === 'number' && qty > addOn.maxQuantity) {
      return { ok: false, reason: 'over_max', lines: [], totalCents: 0 };
    }
    lines.push({
      id: addOn.id,
      label: addOn.label,
      unitPriceCents: addOn.priceCents,
      quantity: qty,
      totalCents: addOn.priceCents * qty,
    });
  }
  return { ok: true, lines, totalCents: lines.reduce((s, l) => s + l.totalCents, 0) };
}

/**
 * #601 — can this party still buy this tier?
 *
 * Lives here, beside quoteEventTicket, for the reason this whole module exists:
 * the widget and the server must not disagree about which tiers are buyable. A
 * widget that greys out a tier the server would have sold loses a booking; one
 * that offers a tier the server refuses wastes a guest's time at the payment
 * step. One predicate, both callers.
 *
 * Only a HARD-allocated tier carries availability. An uncapped tier tracks the
 * event pool, so it is never sold out on its own — it runs out when the event
 * does, which the event-level soldOut flag already says.
 */
export function isTicketTierSoldOut(
  tier: PublicEventTicketTier,
  partySize = 1,
): boolean {
  if (tier.soldOut) return true;
  if (typeof tier.remaining !== 'number') return false;
  return tier.remaining < Math.max(1, partySize);
}

/**
 * #601 — the cheapest per-person price a guest can ACTUALLY buy, for the
 * "From <price> pp" line on an event card. Quoting a sold-out tier's price is a
 * promise the checkout then breaks.
 *
 * When every tier is gone the full range is used again: with nothing buyable,
 * the lowest price is a more honest floor than no price at all — and the card
 * carries its own sold-out badge to say so.
 *
 * Returns null when the event has no tiers (the caller falls back to the base
 * per-person price).
 */
export function tierPriceFloorCents(
  tiers: PublicEventTicketTier[] | undefined | null,
  partySize = 1,
): number | null {
  if (!tiers || tiers.length === 0) return null;
  const sellable = tiers.filter((t) => !isTicketTierSoldOut(t, partySize));
  const pool = sellable.length > 0 ? sellable : tiers;
  return Math.min(...pool.map((t) => t.pricePerPersonCents));
}

/** Covers still available, clamped to ≥ 0. */
export function ticketsRemaining(capacity: number, coversSold: number): number {
  const cap = Number.isFinite(capacity) ? capacity : 0;
  const sold = Number.isFinite(coversSold) ? Math.max(0, coversSold) : 0;
  return Math.max(0, cap - sold);
}

/** True when no covers remain. */
export function isEventSoldOut(capacity: number, coversSold: number): boolean {
  return ticketsRemaining(capacity, coversSold) <= 0;
}

/** True when `partySize` more covers can still be sold. */
export function canFitParty(capacity: number, coversSold: number, partySize: number): boolean {
  if (!Number.isInteger(partySize) || partySize < 1) return false;
  return ticketsRemaining(capacity, coversSold) >= partySize;
}
